// Quick Docs — starred tables and folders on My Desk.
import { authedGet, authedWrite } from './apiClient';
import { useResource, refetchResource, persistResource, peekResource } from './store';

export const FAVORITES_KEY = 'favorites';

export interface FavoriteItem {
  id: number;
  item_key: string;
  kind: 'table' | 'folder' | 'person';
  label: string;
  href: string;
  sort_order: number;
}

export interface FavoriteCatalogItem {
  item_key: string;
  kind: 'table' | 'folder' | 'person';
  label: string;
  href: string;
}

export interface FavoritesData {
  /** The login these pins belong to. A cached list from someone else is dropped. */
  owner?: string;
  favorites: FavoriteItem[];
  catalog: FavoriteCatalogItem[];
}

export const EMPTY_FAVORITES: FavoritesData = { favorites: [], catalog: [] };

/** True only when the cached pins are this login's — never the last person at the desk. */
export function cachedFavoritesAreFor(email: string | null | undefined): boolean {
  if (!email) return false;
  const data = peekResource<FavoritesData>(FAVORITES_KEY);
  return Boolean(data && data.owner === email);
}

export async function fetchFavorites(): Promise<FavoritesData> {
  const data = await authedGet<FavoritesData>('/api/v1/favorites');
  await persistResource(FAVORITES_KEY, data);
  return data;
}

export function useFavorites(): ReturnType<typeof useResource<FavoritesData>> {
  return useResource<FavoritesData>(FAVORITES_KEY, fetchFavorites);
}

export function reloadFavorites(): Promise<void> {
  return refetchResource(FAVORITES_KEY, fetchFavorites);
}

export async function starItem(itemKey: string): Promise<void> {
  await authedWrite('POST', '/api/v1/favorites', { itemKey });
  await reloadFavorites();
}

export async function unstarItem(id: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/favorites/${id}`, {});
  await reloadFavorites();
}

export async function orderFavorites(ids: number[]): Promise<void> {
  await authedWrite('PATCH', '/api/v1/favorites/order', { ids });
  await reloadFavorites();
}

/** Keep the other board's pins in place when one board is reordered. */
export function mergeFavoriteOrder(
  all: FavoriteItem[],
  group: FavoriteItem[],
  nextGroupIds: number[],
): number[] {
  const groupSet = new Set(group.map((item) => item.id));
  const queue = [...nextGroupIds];
  return all.map((item) => (groupSet.has(item.id) ? queue.shift() ?? item.id : item.id));
}
